import {Alert, Box, MenuItem, Skeleton, Stack, TextField} from "@mui/material";
import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {useProfilesQuery} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {useTranslation} from "react-i18next";
import ProviderFactory from "../providers/ProviderFactory";
import {FOLIO_ALERT_SX, FOLIO_FIELD_GAP, FOLIO_LABEL_SX, FOLIO_NOTE_SX} from "./folioStyles";
import {FolioPaymentState, NO_CHARGE} from "./useFolioPayment";

// Product names as their makers write them; anything else as the backend names it
const PROVIDER_NAMES: Record<string, string> = {
    stripe: 'Stripe', hobex: 'Hobex', fias: 'FIAS', asa: 'ASA', apaleo: 'apaleo', mews: 'Mews', casablanca: 'Casablanca',
};
export const providerName = (provider: string) => PROVIDER_NAMES[provider.toLowerCase()] ?? provider;

// Their forms pick the person the charge posts to themselves (Mews: "Post charge to profile")
const OWN_PERSON_PROVIDERS = ['mews'];

interface BillToProps {
    /** The folio's guests, the primary first. */
    personIds: string[]
    value: string
    onChange: (profileId: string) => void
}

// Whom the charge bills, by name - "First Last", like the signing dialog's Guest
const FolioBillToField = ({personIds, value, onChange}: BillToProps) => {
    const {t} = useTranslation();
    const profilesQuery = useProfilesQuery(personIds);

    if (profilesQuery.isLoading) return <Skeleton variant={'rounded'} height={40}/>;

    const nameOf = (profileId: string) => {
        const profile = profilesQuery.data?.find(candidate => candidate.id === profileId);
        const name = [profile?.firstName, profile?.lastName].map(part => part?.trim()).filter(Boolean).join(' ');
        return name || formatProfileListName(profile) || t('Unknown');
    };

    return (
        <TextField
            select
            size={'small'}
            fullWidth
            label={t('Bill to')}
            value={value}
            onChange={event => onChange(event.target.value)}
            InputLabelProps={{shrink: true}}
        >
            {personIds.map(profileId => <MenuItem key={profileId} value={profileId}>{nameOf(profileId)}</MenuItem>)}
        </TextField>
    );
};

interface Props {
    /** A finalized folio: a draft has nothing to pay yet. */
    folio: Folio
    payment: FolioPaymentState
    readOnlyUser: boolean
    /** Whom the charge can bill, the primary first: staff pick one when there are several. */
    personIds: string[]
}

/**
 * A finalized folio's payment, left of its totals where a draft has "Add line": the payment method - a
 * small select -, whom it bills when the folio has several guests, and its provider's form, compact;
 * once settled, how. Nothing when there is nothing to say. The charge itself is the footer's primary
 * action.
 */
const FolioPaymentSection = ({folio, payment, readOnlyUser, personIds}: Props) => {
    const {t} = useTranslation();

    const body = () => {
        if (folio.status !== FolioStatus.OPEN || readOnlyUser) {
            // The totals beside it have the amount
            return folio.provider
                ? <Box sx={{fontSize: 13, lineHeight: '18px'}}>{t('Charged via {{provider}}', {provider: providerName(folio.provider)})}</Box>
                : null;
        }

        const choosesPerson = personIds.length > 1 && !OWN_PERSON_PROVIDERS.includes(payment.provider.toLowerCase());

        return (
            // The provider's first field has its label's room above it, like every field of its form
            <Stack gap={FOLIO_FIELD_GAP}>
                {payment.providersLoading ? (
                    <Skeleton variant={'rounded'} height={40}/>
                ) : payment.providersError ? (
                    <Alert severity={'error'} sx={FOLIO_ALERT_SX}>{t('Failed to load payment providers')}</Alert>
                ) : !payment.canChooseProvider ? (
                    <Box sx={FOLIO_NOTE_SX}>{t('No payment providers available')}</Box>
                ) : (
                    // No label of its own: the block's "Payment" names it - screen readers get the full name
                    <TextField
                        select
                        size={'small'}
                        fullWidth
                        value={payment.provider}
                        onChange={event => payment.selectProvider(event.target.value)}
                        inputProps={{'aria-label': t('Payment method')}}
                    >
                        {payment.providers.map(provider => (
                            <MenuItem key={provider} value={provider}>
                                {provider === NO_CHARGE ? t('No Charge') : providerName(provider)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {payment.provider !== NO_CHARGE && (
                    <>
                        {choosesPerson && (
                            <FolioBillToField personIds={personIds} value={payment.billTo ?? ''} onChange={payment.selectBillTo}/>
                        )}
                        {payment.guestNotStaying && (
                            <Alert severity={'warning'} sx={FOLIO_ALERT_SX}>{t('Guest is not staying currently')}</Alert>
                        )}
                        {/* Keyed: a refresh - or another person billed - starts it over, so it reads what it shows again */}
                        <ProviderFactory
                            key={`${payment.formKey}:${payment.billTo ?? ''}`}
                            provider={payment.provider}
                            profileId={payment.billTo}
                            folio={folio}
                            onDataChange={payment.onProviderData}
                            compact
                        />
                    </>
                )}
            </Stack>
        );
    };

    const content = body();
    if (!content) return null;

    return (
        <Box component={'section'} aria-label={t('Payment')} sx={{maxWidth: 320}}>
            <Box component={'h3'} sx={FOLIO_LABEL_SX}>{t('Payment')}</Box>
            {content}
        </Box>
    );
};

export default FolioPaymentSection;
